import { Directive, Host, Optional, Renderer2, Self, ViewContainerRef, Input, ElementRef } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { Observable, Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";

interface PageObject {
  length: number;
  pageIndex: number;
  pageSize: number;
  previousPageIndex: number;
}

type PageButtonIndex = number | 'pageGap';

@Directive({
  selector: "[style-paginator]",
  standalone: false
})
export class StylePaginatorDirective {
  pageGapTxt = ". . .";
  totalPages!: number;
  lastPageIndex!: number;
  buttons: HTMLElement[] = [];
  currentPageObj: PageObject = {
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0
  };

  // Middle page numbers to display. First and last pages are added separately when needed.
  @Input()
  showTotalPages = 4;

  // optional variable, only to be used together with the filter
  @Input()
  lengthSubject?: Subject<number>;

  get length$(): Observable<number> | undefined {
    return this.lengthSubject?.asObservable();
  }

  actionContainer!: HTMLElement;
  nextPageNode!: HTMLElement;

  constructor(
    @Host() @Self() @Optional() private readonly matPag: MatPaginator,
    private viewContainerRef: ViewContainerRef,
    private render: Renderer2
  ) {
    //  it only runs on page change
    this.matPag.page.subscribe((eventPage: PageObject) => {
      const { pageSize, pageIndex } = this.currentPageObj;
      if (pageSize != eventPage.pageSize && pageIndex != 0) {
        eventPage.pageIndex = 0;
      }
      this.currentPageObj = eventPage;
      this.refreshPageNumbers();
    });
  }

  public ngAfterViewInit(): void {
    this.actionContainer = this.viewContainerRef.element.nativeElement.querySelector(
      "div.mat-mdc-paginator-range-actions"
    );
    this.nextPageNode = this.viewContainerRef.element.nativeElement.querySelector(
      "button.mat-mdc-paginator-navigation-next"
    );

    // it only runs when filtering the fields
    this.length$?.pipe(debounceTime(100)).subscribe(() => {
      this.setTotalPages();
      this.refreshPageNumbers();
     })

    this.setTotalPages();
    this.refreshPageNumbers();
  }

  private setTotalPages(): void {
    this.totalPages = this.matPag.getNumberOfPages();
    this.lastPageIndex = this.totalPages - 1;
  }

  private refreshPageNumbers(): void {
    this.buildPageNumbers();
  }

  private buildPageNumbers(): void {
    this.clearButtons();

    const visiblePageIndexes = this.getVisiblePageIndexes();

    visiblePageIndexes.forEach((buttonPageIndex, index) => {
      const previousPageIndex = visiblePageIndexes[index - 1];

      if (index > 0 && buttonPageIndex - previousPageIndex > 1) {
        this.insertButton(this.createButton('pageGap'));
      }

      this.insertButton(this.createButton(buttonPageIndex, this.matPag.pageIndex));
    });
  }

  private clearButtons(): void {
    this.buttons.forEach(button => {
      this.render.removeChild(this.actionContainer, button);
    });
    this.buttons.length = 0;
  }

  private getVisiblePageIndexes(): number[] {
    if (this.totalPages <= this.showTotalPages + 2) {
      return Array.from({ length: this.totalPages }, (_, index) => index);
    }

    return [0, ...this.getVisibleMiddlePageIndexes(), this.lastPageIndex];
  }

  private getVisibleMiddlePageIndexes(): number[] {
    const firstMiddlePageIndex = 1;
    const lastMiddlePageIndex = this.lastPageIndex - 1;
    const middlePageCount = this.showTotalPages;
    const centeredRangeStart = this.matPag.pageIndex - Math.floor((middlePageCount - 1) / 2);
    const lastAvailableRangeStart = lastMiddlePageIndex - middlePageCount + 1;
    const rangeStart = this.clamp(centeredRangeStart, firstMiddlePageIndex, lastAvailableRangeStart);
    const rangeEnd = Math.min(lastMiddlePageIndex, rangeStart + middlePageCount - 1);

    return Array.from(
      { length: rangeEnd - rangeStart + 1 },
      (_, index) => rangeStart + index
    );
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  private insertButton(createdButton: HTMLElement): void {
    this.render.insertBefore(
      this.actionContainer,
      createdButton,
      this.nextPageNode
    );
  }

  private createButton(buttonPageIndex: PageButtonIndex, pageIndex?: number): HTMLElement {
    const button = this.render.createElement("span");
    const pagingTxt = buttonPageIndex === 'pageGap' ? this.pageGapTxt : buttonPageIndex + 1;
    this.render.addClass(button, "style-paginator-page");
    this.render.setStyle(button, "margin", ".5rem");
    this.render.setStyle(button, "cursor", "pointer");
    this.render.setStyle(button, "user-select", "none");
    this.render.setStyle(button, "width", "max-content");

    switch (buttonPageIndex) {
      case pageIndex:
        this.render.addClass(button, "current-page");
        this.render.setAttribute(button, "aria-current", "page");
        this.render.setAttribute(button, "disabled", "disabled");
        this.render.setStyle(button, "color", "var(--dark-base-color)");
        this.render.setStyle(button, "font-weight", "500");
        this.render.setStyle(button, "cursor", "default");
        break;
      case 'pageGap':
        this.render.addClass(button, "page-gap");
        this.render.setStyle(button, "cursor", "default");
        break;
      default:
        this.addClickEventListener(button, buttonPageIndex);
        break;
    }
    this.render.appendChild(button, this.render.createText(pagingTxt.toString()));
    this.buttons.push(button);
    return button;
  }

  private addClickEventListener(button: ElementRef<HTMLElement>, buttonPageIndex: number):void {
    this.render.listen(button, "click", () => {
      this.switchPage(buttonPageIndex);
    });
  }

  private switchPage(buttonPageIndex: number): void {
    const previousPageIndex = this.matPag.pageIndex;
    this.matPag.pageIndex = buttonPageIndex;
    this.matPag["_emitPageEvent"](previousPageIndex);
    this.refreshPageNumbers();
  }

}
